import { Controller } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import Button from '@/components/Button';
import DateRangePicker from '@/components/DateRangePicker';
import FileInput from '@/components/FileInput';
import SchoolsFilter from '@/components/SchoolsFilter';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useAnnouncementsForm } from '../../hooks/useAnnouncementsForm';
import { AnnouncementFormValues, useAnnouncementStore } from '../../store/announcementStore';

// Configure dayjs to use timezone
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Europe/London');

export const AnnouncementForm = () => {
  const { page } = useAnnouncementStore();
  const { control, handleSubmit, setValue, errors, onSubmit, logoFile, fileInputRef, isEditing, isSubmitting, watch } =
    useAnnouncementsForm();

  const description = watch('description') || '';
  const characterCount = description.length;
  const maxCharacters = 300;

  // Helper function to determine if errors should be shown
  const shouldShowError = (fieldName: keyof AnnouncementFormValues): boolean => {
    return !!errors[fieldName];
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <div>
        <h2 className="text-sh-sm font-bold">{page?.form?.schoolsTitle || 'Schools'}</h2>
        {page?.form?.schoolsDescription && <p className="text-sh-xss">{page?.form?.schoolsDescription}</p>}
        <Controller
          control={control}
          name="schoolIds"
          render={({ field }) => (
            <SchoolsFilter
              selectedSchoolsIds={field.value || []}
              onChange={(selectedIds) => field.onChange(selectedIds)}
              showChips
              className="w-full"
              asRegularUser={true}
            />
          )}
        />
        {shouldShowError('schoolIds') && errors.schoolIds && (
          <span className="mt-1 block text-xs text-red-500">{errors.schoolIds?.message as string}</span>
        )}
      </div>

      <div className="mt-sh-30">
        <h2 className="text-sh-sm font-bold">{page?.form?.datesTitle}</h2>
        <p className="text-sh-xss">{page?.form?.datesDescription}</p>
      </div>

      <div>
        <div className="flex flex-col">
          <Controller
            control={control}
            name="startDate"
            render={({ field: startDateField }) => (
              <Controller
                control={control}
                name="endDate"
                render={({ field: endDateField }) => (
                  <DateRangePicker
                    startDate={dayjs(startDateField.value)}
                    endDate={dayjs(endDateField.value)}
                    onStartDateChange={(date) => startDateField.onChange(date?.tz('Europe/London').toDate())}
                    onEndDateChange={(date) => endDateField.onChange(date?.tz('Europe/London').toDate())}
                  />
                )}
              />
            )}
          />
          {shouldShowError('startDate') && errors.startDate && (
            <span className="mt-1 block text-xs text-red-500">{errors.startDate.message as string}</span>
          )}
          {shouldShowError('endDate') && errors.endDate && (
            <span className="mt-1 block text-xs text-red-500">{errors.endDate.message as string}</span>
          )}
        </div>
      </div>

      <div className="mt-sh-30">
        <h2 className="mb-2 text-sh-sm font-bold">{page?.form?.logoTitle}</h2>
        <p className="mb-4 text-sh-xss">{page?.form?.logoDescription}</p>

        <Controller
          control={control}
          name="logo"
          render={({ field }) => (
            <FileInput
              id="logo"
              accept="image/png"
              ref={fileInputRef}
              value={logoFile}
              onChange={(file) => {
                if (file) {
                  field.onChange(file);
                  setValue('logo', file);
                }
              }}
              placeholder={page?.form?.logoUploadPrompt || 'Upload a logo'}
              buttonText={page?.form?.logoUploadBtn || 'Upload'}
              dragActiveText="Drop your PNG file here"
              error={errors.logo?.message as string}
            />
          )}
        />
      </div>

      <div className="mt-sh-30">
        <h2 className="mb-2 text-sh-sm font-bold">{page?.form?.descriptionTitle}</h2>
        <p className="mb-4 text-sh-xss">{page?.form?.descriptionSubtitle}</p>

        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <Input
              as="textarea"
              placeholder={page?.form?.descriptionPlaceholder || undefined}
              className={`${shouldShowError('description') ? 'border-red-500' : ''} min-h-[150px] text-sh-xss placeholder:text-sh-xss`}
              {...field}
            />
          )}
        />
        <div className="flex items-center justify-between">
          {shouldShowError('description') && errors.description && (
            <span className="block text-xs text-red-500">{errors.description.message as string}</span>
          )}
          <div>
            <span className={`text-xs ${characterCount > maxCharacters ? 'text-red-500' : 'text-gray-500'}`}>
              {characterCount}/{maxCharacters} {page?.form?.charactersLabel}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-sh-30">
        <Button type="submit" theme="solid" disabled={isSubmitting}>
          {isSubmitting ? page?.form?.savingBtn : isEditing ? page?.form?.updateBtn : page?.form?.createBtn}
        </Button>
      </div>
    </form>
  );
};
